import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const parse = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/** S1: the two discount limits that decide whether a quotation needs approval before it can be sent. */
const DiscountLimitsCard: React.FC = () => {
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
  const [rep, setRep] = useState('');
  const [mgr, setMgr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution();
    if (!institutionId) return;
    Promise.resolve(SalesSettingsService.getDiscountLimits(institutionId))
      .then(l => {
        setRep(l?.repMaxDiscountPct != null ? String(l.repMaxDiscountPct) : '');
        setMgr(l?.managerMaxDiscountPct != null ? String(l.managerMaxDiscountPct) : '');
      })
      .catch(() => { /* leaving the fields empty means "no limit" */ });
  }, []);

  const repN = parse(rep);
  const mgrN = parse(mgr);
  const outOfRange = (n: number | null) => n != null && (n < 0 || n > 100);
  const error = outOfRange(repN) || outOfRange(mgrN) ? 'A limit must be between 0 and 100.'
    : repN != null && mgrN != null && mgrN < repN ? 'The manager limit cannot be below the salesperson limit.' : '';

  const save = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    if (!institutionId || error) return;
    try {
      setSaving(true);
      await SalesSettingsService.saveDiscountLimits(institutionId, { repMaxDiscountPct: repN, managerMaxDiscountPct: mgrN });
      toast.showSuccess('Discount limits saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save the discount limits');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="discount-limits-card" data-help-section="sales.discountLimits.section">
      <Typography variant="h6">Quotation discount approval</Typography>
      <Typography variant="body2" color="text.secondary">
        A quotation whose largest line discount is above the salesperson limit needs its team manager's approval before it can be sent.
        Above the manager limit it goes to the department head. Leave both empty for no approval.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <TextField data-help="sales.discountLimits.rep" label="Salesperson max discount (%)" type="number" value={rep} disabled={!canManage}
          onChange={e => setRep(e.target.value)} inputProps={{ min: 0, max: 100, step: 0.01, 'aria-label': 'Salesperson max discount' }}
          sx={{ flex: '1 1 200px', maxWidth: 300 }} />
        <TextField data-help="sales.discountLimits.manager" label="Manager max discount (%)" type="number" value={mgr} disabled={!canManage}
          onChange={e => setMgr(e.target.value)} inputProps={{ min: 0, max: 100, step: 0.01, 'aria-label': 'Manager max discount' }}
          sx={{ flex: '1 1 200px', maxWidth: 300 }} />
      </Box>
      {error && <Typography variant="caption" color="error">{error}</Typography>}
      {canManage && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button data-help="sales.discountLimits.save" variant="contained" onClick={save} disabled={saving || !!error}>Save discount limits</Button>
        </Box>
      )}
    </Paper>
  );
};

export default DiscountLimitsCard;
